/**
 * Click handler and paste handler.
 * Verbatim slice of the iframe bridge script (optimization Phase 10c) —
 * returned text is embedded inside the bridge IIFE; it is NOT module code.
 * The slice is interpolation-free: keep it that way (head/tail interpolations
 * live in iframe-bridge.js).
 */

export function interactionLayer() {
  return String.raw`  // Tier 4 Phase 5: embeds carry pointer-events:none in edit mode, so a
  // click over a map/iframe lands on an ancestor. Hit-test descendant embeds
  // against the actual click point to recover the intended target.
  function resolveEmbedAtPoint(rawTarget, evt) {
    var el = rawTarget && rawTarget.nodeType === 1 ? rawTarget : (rawTarget && rawTarget.parentElement);
    if (!el || !el.querySelectorAll || !evt || typeof evt.clientX !== 'number') return null;
    if (!el.closest || !el.closest('[data-pb-instance]')) return null;
    var embeds = el.querySelectorAll('[data-pb-editable="embed"]');
    for (var i = 0; i < embeds.length; i++) {
      var rect = embeds[i].getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) continue;
      if (evt.clientX >= rect.left && evt.clientX <= rect.right &&
          evt.clientY >= rect.top && evt.clientY <= rect.bottom) {
        return embeds[i];
      }
    }
    return null;
  }

  function firstCssBackgroundUrl(value) {
    var raw = String(value || '');
    if (!raw || raw === 'none') return '';
    var match = /url\((['"]?)(.*?)\1\)/i.exec(raw);
    var url = match ? String(match[2] || '').trim() : '';
    if (!url || /^data:/i.test(url) || /^var\(/i.test(url)) return '';
    return url;
  }

  function normalizeCssBackgroundUrl(url) {
    var raw = String(url || '').trim();
    if (!raw) return '';
    try {
      return new URL(raw, window.location.href).href;
    } catch (_) {
      return raw;
    }
  }

  function cssBackgroundUrlsMatch(a, b) {
    var left = normalizeCssBackgroundUrl(a);
    var right = normalizeCssBackgroundUrl(b);
    return !!left && !!right && left === right;
  }

  function collectRuleBackgroundUrls(rule, sectionId, out) {
    if (!rule) return;
    var nested = null;
    try { nested = rule.cssRules; } catch (_) { nested = null; }
    if (nested && nested.length) {
      for (var ni = 0; ni < nested.length; ni++) collectRuleBackgroundUrls(nested[ni], sectionId, out);
      return;
    }
    var selector = String(rule.selectorText || '');
    if (!selector || selector.indexOf('[data-pb-section') === -1 || selector.indexOf(sectionId) === -1) return;
    var style = rule.style;
    if (!style || typeof style.getPropertyValue !== 'function') return;
    var values = [
      style.getPropertyValue('background-image'),
      style.getPropertyValue('background')
    ];
    for (var vi = 0; vi < values.length; vi++) {
      var value = String(values[vi] || '');
      var re = /url\((['"]?)(.*?)\1\)/gi;
      var match;
      while ((match = re.exec(value))) {
        var url = String(match[2] || '').trim();
        if (!url || /^data:/i.test(url) || /^var\(/i.test(url)) continue;
        out.push(url);
      }
    }
  }

  function sectionCssBackgroundIndex(sectionId, computedUrl) {
    if (!sectionId || !computedUrl) return -1;
    var urls = [];
    for (var si = 0; si < document.styleSheets.length; si++) {
      var rules = null;
      try { rules = document.styleSheets[si].cssRules; } catch (_) { rules = null; }
      if (!rules) continue;
      for (var ri = 0; ri < rules.length; ri++) collectRuleBackgroundUrls(rules[ri], sectionId, urls);
    }
    for (var ui = 0; ui < urls.length; ui++) {
      if (cssBackgroundUrlsMatch(urls[ui], computedUrl)) return ui;
    }
    return -1;
  }

  function sectionIdForWrapper(wrapper) {
    if (!wrapper) return '';
    var sectionId = wrapper.getAttribute('data-pb-section') || '';
    if (sectionId) return sectionId;
    var sectionNode = wrapper.matches && wrapper.matches('[data-pb-section]')
      ? wrapper
      : (wrapper.querySelector ? wrapper.querySelector('[data-pb-section]') : null);
    return sectionNode ? (sectionNode.getAttribute('data-pb-section') || '') : '';
  }

  function resolveCssBackgroundClickTarget(evt, directEditable, decorativeEditable) {
    if (!evt || directEditable || decorativeEditable) return null;
    var rawTarget = evt.target && evt.target.nodeType === 1 ? evt.target : (evt.target && evt.target.parentElement);
    if (!rawTarget || !rawTarget.closest) return null;
    var wrapper = rawTarget.closest('[data-pb-instance]');
    if (!wrapper) return null;
    var sectionId = sectionIdForWrapper(wrapper);
    if (!sectionId) return null;

    var current = rawTarget;
    while (current && current.nodeType === 1) {
      var rect = current.getBoundingClientRect ? current.getBoundingClientRect() : null;
      if (rect && rect.width > 1 && rect.height > 1 &&
          evt.clientX >= rect.left && evt.clientX <= rect.right &&
          evt.clientY >= rect.top && evt.clientY <= rect.bottom) {
        var pseudos = ['', '::before', '::after'];
        for (var pi = 0; pi < pseudos.length; pi++) {
          var style = null;
          try { style = getComputedStyle(current, pseudos[pi] || null); } catch (_) { style = null; }
          var url = firstCssBackgroundUrl(style && style.backgroundImage);
          var cssBgIndex = sectionCssBackgroundIndex(sectionId, url);
          if (cssBgIndex >= 0) {
            return {
              instanceId: wrapper.getAttribute('data-pb-instance') || '',
              sectionId: sectionId,
              editableType: 'css-background',
              kind: 'background',
              targetRef: {
                domain: 'section-css',
                key: sectionId + ':' + cssBgIndex,
                locator: { sectionId: sectionId, ordinal: cssBgIndex },
                expectedFingerprint: url
              },
              cssBgIndex: cssBgIndex,
              imgSrc: url,
              tagName: 'CSS-BACKGROUND',
              rect: {
                top: rect.top + window.scrollY,
                left: rect.left,
                width: rect.width,
                height: rect.height
              },
              editIntent: false
            };
          }
        }
      }
      if (current === wrapper) break;
      current = current.parentElement;
    }
    return null;
  }

  // CSS backgrounds have no DOM node to put in the selected-editable slot, but paste
  // still needs the same selected-target memory as every other graphic.
  var _selectedCssBackgroundPath = null;

  // Tier 4 Phase 5: in edit mode a mousedown on a form-field editable must
  // SELECT it, not focus the native control (focus happens on mousedown, so
  // preventDefault here is the only reliable way to stop caret/typing).
  document.addEventListener('mousedown', function(e) {
    if (_previewMode) return;
    var fieldEditable = e.target && e.target.closest && e.target.closest('[data-pb-editable="field"]');
    if (fieldEditable) e.preventDefault();
  });

  // --- Click handler ---
  document.addEventListener('click', function(e) {
    if (_recentlyDragged && performance.now() - _recentlyDragged < 250) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    if (_previewMode) {
      var darkToggle = e.target.closest('[id^="dark-mode-toggle"]');
      if (darkToggle) {
        e.preventDefault();
        e.stopPropagation();
        _toggleDarkMode(e);
        return;
      }

      var csToggle = e.target.closest('.cs-toggle');
      if (csToggle && _toggleCodeStitchNav(csToggle)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // A click on a dropdown's own child link should navigate, not collapse it.
      var csDropdown = e.target.closest('.cs-dropdown');
      var csDropLink = e.target.closest('.cs-drop-link');
      if (csDropdown && !csDropLink && _toggleCodeStitchDropdown(csDropdown)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      var hamburger = e.target.closest('.hamburger-menu');
      if (hamburger && _toggleStarterNav(hamburger)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Intercept internal page links — navigate within the builder
      var anchor = e.target.closest('a[href]');
      if (anchor) {
        var href = anchor.getAttribute('href') || '';
        // Skip mailto, tel, and hash-only anchors — let default behavior handle
        if (href.startsWith('mailto:') || href.startsWith('tel:') || href === '#') {
          return;
        }
        // External links — open in parent window's new tab
        if (href.startsWith('http://') || href.startsWith('https://')) {
          e.preventDefault();
          e.stopPropagation();
          send('open-external-link', { href: href });
          return;
        }
        // Internal relative link — send to parent for page navigation
        if (href.startsWith('/') || (!href.includes('://') && !href.startsWith('#'))) {
          e.preventDefault();
          e.stopPropagation();
          send('navigate-page', { href: href });
          return;
        }
      }

      return; // Let all other clicks pass through normally
    }

    var directEditable = resolveEditableClickTarget(e.target, e);
    // Tier 4 Phase 5: embeds get pointer-events:none in edit mode (an iframe
    // otherwise swallows the click into its own document), so the click lands
    // on an ancestor. Manually hit-test descendant embeds against the click
    // point when nothing (or only a section-level target) resolved directly.
    if (!directEditable) {
      directEditable = resolveEmbedAtPoint(e.target, e);
    }
    var decorativeEditable = resolveDecorativeGraphicAtPoint(e, directEditable);
    var editable = decorativeEditable || directEditable;
    if (editable) {
      if (activeEditable && isTextEditable(activeEditable) && (editable === activeEditable || activeEditable.contains(e.target))) {
        var activeLink = e.target.closest && e.target.closest('a[href]');
        if (activeLink && activeEditable.contains(activeLink)) e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      e.preventDefault();
      // stopImmediatePropagation so section JS click handlers (e.g. the
      // special-9001/9002 reel lightbox) don't also fire alongside the editor.
      e.stopImmediatePropagation();
      var path = getEditablePath(editable);
      if (!path) return;
      path.editIntent = e.detail >= 2;
      var pathWrapper = editable.closest('[data-pb-instance]');
      path.classNames = editable.classList ? Array.prototype.slice.call(editable.classList) : [];
      path.ancestorChain = buildElementAncestorChain(editable, pathWrapper);
      // 16C1 — modifier forwarding for sibling multi-select. Gated on NOT
      // editing: inside a live contenteditable, Shift-click must keep extending
      // the text selection rather than resolving to a selection gesture.
      if (!activeEditable) {
        path.metaKey = !!(e.metaKey || e.ctrlKey);
        path.shiftKey = !!e.shiftKey;
      }
      var editableStyle = getComputedStyle(editable);
      var rootStyle = getComputedStyle(document.documentElement);
      addComputedStyleMeta(path, editable, editableStyle, rootStyle);
      var parent = editable.parentElement;
      if (parent) {
        var parentStyle = getComputedStyle(parent);
        path.parentComputedDisplay = parentStyle.display || '';
        path.parentComputedTextAlign = parentStyle.textAlign || '';
        path.parentComputedJustifyContent = parentStyle.justifyContent || '';
        path.parentComputedAlignItems = parentStyle.alignItems || '';
        path.parentComputedMarginLeft = parentStyle.marginLeft || '';
        path.parentComputedMarginRight = parentStyle.marginRight || '';
      }

      if (path.editableType === 'image') {
        // The parent-side active-editable store keeps this whole payload. Alt
        // state therefore belongs here beside the image identity rather than
        // being guessed by whichever surface happens to render a warning.
        // getAttribute preserves the meaningful distinction between a
        // missing attribute (null) and an authored empty one ("").
        path.alt = editable.getAttribute('alt');
        var pic = editable.parentElement && editable.parentElement.tagName === 'PICTURE'
          ? editable.parentElement
          : editable.closest('picture');
        if (pic) {
          var pictureStyle = getComputedStyle(pic);
          path.pictureComputedAspectRatio = pictureStyle.aspectRatio || '';
          path.pictureComputedBorderRadius = pictureStyle.borderRadius || '';
          path.pictureComputedBoxShadow = pictureStyle.boxShadow || '';
          path.pictureComputedDisplay = pictureStyle.display || '';
          path.pictureComputedWidth = pictureStyle.width || '';
          path.pictureComputedMaxWidth = pictureStyle.maxWidth || '';
          path.pictureComputedMarginLeft = pictureStyle.marginLeft || '';
          path.pictureComputedMarginRight = pictureStyle.marginRight || '';
          path.pictureComputedTextAlign = pictureStyle.textAlign || '';
          path.pictureComputedJustifyContent = pictureStyle.justifyContent || '';
          path.pictureComputedAlignItems = pictureStyle.alignItems || '';
          path.pictureComputedPosition = pictureStyle.position || '';
          path.pictureComputedLeft = pictureStyle.left || '';
        }
      }

      if (path.editableType === 'svg') {
        path.svgPreserveAspectRatio = editable.getAttribute('preserveAspectRatio') || '';
      }

      // For buttons and text links, include link info from the <a>
      // (editable itself or ancestor wrapper).
      if (path.editableType === 'button' || path.editableType === 'text') {
        var anchor = editable.tagName === 'A' ? editable : editable.closest('a');
        if (anchor) {
          path.href = anchor.getAttribute('href') || '';
          path.target = anchor.getAttribute('target') || '';
          // Buttons: the <a> IS the styled control, so its computed style is the
          // authoritative one — re-read it. Text (e.g. a heading inside a card
          // link): the anchor is only a link wrapper. The heading's own
          // getComputedStyle (captured above) already resolves inheritance from
          // the anchor, so it must NOT be overwritten with the anchor's values —
          // doing so anchored size chips to the link's font-size (shrinking large
          // headings) and mis-reported the color. Text keeps its own computed*.
          if (path.editableType === 'button') {
            addComputedStyleMeta(path, anchor, getComputedStyle(anchor), rootStyle);
          }
        }
      }
      _setSelectedSection(path.instanceId, { flash: false });
      _selectedCssBackgroundPath = null;
      send('element-clicked', path);
      // Send element rect for floating panel positioning
      var elRect = editable.getBoundingClientRect();
      var elScrollY = window.scrollY;
      var elZoom = getCurrentZoom();
      var elComputedStyle = getComputedStyle(editable);
      var elContainer = editable.closest('.cs-container') || editable.closest('[data-pb-instance]');
      var elContainerRect = elContainer ? elContainer.getBoundingClientRect() : null;
      var elPic = editable.parentElement && editable.parentElement.tagName === 'PICTURE'
        ? editable.parentElement : null;
      if (!elPic && editable.closest) elPic = editable.closest('picture');
      var elPicRect = elPic ? elPic.getBoundingClientRect() : null;
      var elPicStyle = elPic ? getComputedStyle(elPic) : null;
      _setSelectedEditable(editable);
      var elResolvedHost = _resolveSelectedStateHost(editable);

      var elRectPayload = {
        instanceId: path.instanceId,
        eid: path.eid,
        editableType: path.editableType,
        kind: path.kind,
        targetRef: path.targetRef,
        tagName: (editable.tagName || '').toUpperCase(),
        label: _editableDisplayLabel(editable, path.editableType),
        requiresParentPill: !!(elResolvedHost && elResolvedHost.requiresParentPill),
        requiresParentHoverPill: !!(elResolvedHost && elResolvedHost.requiresParentHoverPill),
        rect: { top: elRect.top + elScrollY, left: elRect.left, width: elRect.width, height: elRect.height },
        zoom: elZoom,
        spacing: {
          paddingTop: elComputedStyle.paddingTop,
          paddingRight: elComputedStyle.paddingRight,
          paddingBottom: elComputedStyle.paddingBottom,
          paddingLeft: elComputedStyle.paddingLeft,
          marginTop: elComputedStyle.marginTop,
          marginRight: elComputedStyle.marginRight,
          marginBottom: elComputedStyle.marginBottom,
          marginLeft: elComputedStyle.marginLeft
        },
        spacingTemplate: getSpacingTemplateForEditable(editable),
        positioning: {
          position: elComputedStyle.position,
          left: elComputedStyle.left,
          right: elComputedStyle.right
        },
        containerWidth: elContainerRect ? elContainerRect.width : null,
        containerLeft: elContainerRect ? elContainerRect.left : null,
        pictureRect: elPicRect ? { top: elPicRect.top + elScrollY, left: elPicRect.left, width: elPicRect.width, height: elPicRect.height } : null,
        picturePositioning: elPicStyle ? {
          paddingTop: elPicStyle.paddingTop,
          paddingRight: elPicStyle.paddingRight,
          paddingBottom: elPicStyle.paddingBottom,
          paddingLeft: elPicStyle.paddingLeft,
          marginTop: elPicStyle.marginTop,
          marginRight: elPicStyle.marginRight,
          marginBottom: elPicStyle.marginBottom,
          marginLeft: elPicStyle.marginLeft,
          position: elPicStyle.position,
          left: elPicStyle.left,
          right: elPicStyle.right
        } : null
      };
      elRectPayload.ancestry = getAncestryForEditable(editable);
      addLogoRectPayload(elRectPayload, editable, elScrollY);
      send('element-rect', elRectPayload);
      if (e.detail === 2 && /^(?:photo|logo|icon|decoration|background)$/.test(path.kind || '')) {
        send('element-primary-action', path);
      }
      return;
    }

    var cssBackgroundPath = resolveCssBackgroundClickTarget(e, directEditable, decorativeEditable);
    if (cssBackgroundPath) {
      e.preventDefault();
      e.stopImmediatePropagation();
      cssBackgroundPath.editIntent = e.detail >= 2;
      _setSelectedEditable(null);
      _selectedCssBackgroundPath = cssBackgroundPath;
      _setSelectedSection(cssBackgroundPath.instanceId, { flash: false });
      send('element-clicked', cssBackgroundPath);
      send('element-rect', {
        instanceId: cssBackgroundPath.instanceId,
        eid: null,
        targetRef: cssBackgroundPath.targetRef,
        editableType: cssBackgroundPath.editableType,
        kind: cssBackgroundPath.kind,
        tagName: cssBackgroundPath.tagName,
        label: KIND_LABELS.background || 'Background',
        rect: cssBackgroundPath.rect,
        zoom: getCurrentZoom()
      });
      if (e.detail === 2) send('element-primary-action', cssBackgroundPath);
      return;
    }

    var sectionWrapper = e.target.closest('[data-pb-instance]');
    if (sectionWrapper) {
      var clickedSectionInstanceId = sectionWrapper.getAttribute('data-pb-instance');
      _setSelectedSection(clickedSectionInstanceId, { flash: true });
      send('section-clicked', { instanceId: clickedSectionInstanceId });
    } else {
      _clearSelectedSection('canvas-click');
      send('canvas-clicked');
    }
    clearSelectedEditable('canvas-or-section-click');
    _selectedCssBackgroundPath = null;

    if (activeEditable && !e.target.closest('[data-pb-editable]')) {
      finishEditing();
    }
  }, true);

  document.addEventListener('dblclick', function(e) {
    if (_previewMode) return;
    if (_recentlyDragged && performance.now() - _recentlyDragged < 250) return;
    if (e.button && e.button !== 0) return;

    var directEditable = resolveEditableClickTarget(e.target, e);
    var decorativeEditable = resolveDecorativeGraphicAtPoint(e, directEditable);
    if (directEditable && directEditable.getAttribute('data-pb-editable') === 'text') {
      var editPath = getEditablePath(directEditable);
      if (!editPath) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      editPath.editIntent = true;
      send('text-edit-requested', editPath);
      return;
    }
    if (directEditable || decorativeEditable) return;
    if (e.target.closest && e.target.closest('a, button, input, textarea, select, [contenteditable="true"]')) return;

    var sectionWrapper = e.target.closest ? e.target.closest('[data-pb-instance]') : null;
    if (!sectionWrapper) return;
    var sectionInstanceId = sectionWrapper.getAttribute('data-pb-instance');
    if (!sectionInstanceId) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    _setSelectedSection(sectionInstanceId, { flash: true });
    clearSelectedEditable('section-double-click');
    _selectedCssBackgroundPath = null;
    send('section-double-clicked', { instanceId: sectionInstanceId });
  }, true);

  function _getSelectionSafe() {
    try {
      return window.getSelection ? window.getSelection() : null;
    } catch (err) {
      return null;
    }
  }

  function _selectionRangeInsideElement(el, selection) {
    if (!el || !selection || !selection.rangeCount) return false;
    try {
      return el.contains(selection.getRangeAt(0).commonAncestorContainer);
    } catch (err) {
      return false;
    }
  }

  function _shouldLetTextSelectionOwnContextMenu() {
    var selection = _getSelectionSafe();
    return !!(
      activeEditable &&
      selection &&
      selection.rangeCount &&
      !selection.isCollapsed &&
      _selectionRangeInsideElement(activeEditable, selection)
    );
  }

  function _clearSelectionOutsideActiveTextEdit() {
    var selection = _getSelectionSafe();
    if (!selection || !selection.rangeCount) return;
    if (activeEditable && !selection.isCollapsed && _selectionRangeInsideElement(activeEditable, selection)) return;
    try {
      selection.removeAllRanges();
    } catch (err) { console.warn('[pb-bridge] selection clear failed:', err); }
  }

  function _resolveElementContextMenuTarget(rawTarget) {
    var target = rawTarget && rawTarget.nodeType === 1 ? rawTarget : (rawTarget && rawTarget.parentElement);
    var editable = (target && target.closest) ? target.closest('[data-pb-editable]') : null;
    if (!editable) return null;
    var eidEl = editable.hasAttribute('data-pb-eid')
      ? editable
      : editable.closest('[data-pb-eid]');
    if (!eidEl) return null;
    var sectionEl = eidEl.closest('[data-pb-instance]');
    if (!sectionEl) return null;
    return {
      editable: editable,
      eidEl: eidEl,
      sectionEl: sectionEl
    };
  }

  // Prevent the browser from turning a structural right-click into a native
  // word selection before our custom contextmenu handler gets the event.
  // Suppresses default for decorative / section / element intents; lets text
  // selections and dead-zone clicks fall through to the browser.
  document.addEventListener('mousedown', function(e) {
    if (e.button !== 2) return;
    var intent = _resolveContextMenuIntent(e);
    if (intent.kind !== 'decorative' && intent.kind !== 'section' && intent.kind !== 'element') return;
    e.preventDefault();
    _clearSelectionOutsideActiveTextEdit();
  }, true);

  // Decorative / section right-click dispatcher (capture phase). Runs before
  // the element-context handler below so it can intercept decoratives that
  // also carry [data-pb-editable]. Uses the same _resolveContextMenuIntent
  // helper as the mousedown gate so the two cannot drift apart.
  document.addEventListener('contextmenu', function(e) {
    var intent = _resolveContextMenuIntent(e);
    if (intent.kind === 'decorative') {
      e.preventDefault();
      e.stopImmediatePropagation();
      _clearSelectionOutsideActiveTextEdit();
      var decoDesc = _buildDecorativeDescriptor(intent.deco, e, intent.sectionEl);
      if (decoDesc) send('request-decorative-menu', decoDesc);
      return;
    }
    if (intent.kind === 'section') {
      e.preventDefault();
      e.stopImmediatePropagation();
      _clearSelectionOutsideActiveTextEdit();
      var sectionDesc = _buildSectionMenuDescriptor(intent.sectionEl, e);
      if (sectionDesc) send('request-section-menu', sectionDesc);
      return;
    }
    // text-selection / element / null intents fall through to the legacy
    // handlers below.
  }, true);

  // Element-context contextmenu (Phase 3 of element-insertion engine).
  // Fires for right-clicks on any [data-pb-editable] EXCEPT when the existing
  // text-bind-menu handler below would own the event (active text edit with a
  // non-collapsed selection). Sends a request-element-menu postMessage with
  // a flat ancestor chain so the parent can resolve a pattern adapter without
  // reaching into the iframe DOM.
  document.addEventListener('contextmenu', function(e) {
    // Defer to the existing text-bind-menu path when a text selection is active.
    if (_shouldLetTextSelectionOwnContextMenu()) return;

    var contextTarget = _resolveElementContextMenuTarget(e.target);
    if (!contextTarget) return;
    var eidEl = contextTarget.eidEl;
    var sectionEl = contextTarget.sectionEl;

    e.preventDefault();
    e.stopImmediatePropagation();
    _clearSelectionOutsideActiveTextEdit();

    var chain = buildElementAncestorChain(eidEl, sectionEl);

    var ctxRect = eidEl.getBoundingClientRect();
    send('request-element-menu', {
      instanceId: sectionEl.getAttribute('data-pb-instance'),
      eid: eidEl.getAttribute('data-pb-eid'),
      editableType: eidEl.getAttribute('data-pb-editable') || '',
      tagName: (eidEl.tagName || '').toLowerCase(),
      // The same human name the hover pill shows — only the iframe can compute
      // it (it consults layout), and menu actions need it for their labels.
      label: _editableDisplayLabel(eidEl, eidEl.getAttribute('data-pb-editable') || ''),
      classNames: eidEl.classList ? Array.prototype.slice.call(eidEl.classList) : [],
      ancestorChain: chain,
      rect: {
        top: ctxRect.top,
        left: ctxRect.left,
        width: ctxRect.width,
        height: ctxRect.height
      },
      point: {
        top: e.clientY,
        left: e.clientX
      },
      zoom: getCurrentZoom()
    });
  }, true);

  document.addEventListener('contextmenu', function(e) {
    if (_plainTextOnly) return;
    if (!_shouldLetTextSelectionOwnContextMenu()) return;
    var sel = _getSelectionSafe();
    if (!sel || !sel.rangeCount) return;
    var range = sel.getRangeAt(0);

    e.preventDefault();
    e.stopPropagation();

    var rect = range.getBoundingClientRect();
    var text = (sel.toString() || '').replace(/\s+/g, ' ').trim();
    send('request-bind-menu', {
      selectionText: text,
      rect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      },
      eid: activeEditable.getAttribute('data-pb-eid') || null,
      editSessionId: _activeEditSessionId,
      zoom: getCurrentZoom()
    });
  }, true);

  document.addEventListener('dragstart', function(e) {
    if (!activeEditable || !isTextEditable(activeEditable)) return;
    var target = e.target;
    if (target === activeEditable || (target && activeEditable.contains(target))) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    var activeAnchor = activeEditable.closest ? activeEditable.closest('a[href]') : null;
    if (activeAnchor && (target === activeAnchor || activeAnchor.contains(target))) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  // --- Paste handler ---
  function _emitPasteTriageStatus(profile, result) {
    send('paste-triage-status', {
      profile: profile || DEFAULT_PASTE_PROFILE,
      rejected: !!(result && result.rejected),
      truncated: !!(result && result.truncated),
      reason: result && result.reason ? result.reason : '',
      eid: activeEditable ? (activeEditable.getAttribute('data-pb-eid') || null) : null,
      editSessionId: _activeEditSessionId
    });
  }

  function _sanitizePastePayload(payload, profile) {
    if (!PASTE_SANITIZER || typeof PASTE_SANITIZER.sanitizeClipboardPayload !== 'function') {
      return {
        ok: true,
        rejected: false,
        truncated: false,
        reason: '',
        profile: profile || DEFAULT_PASTE_PROFILE,
        html: payload && payload.html
          ? String(payload.html)
          : (String(payload && payload.text ? payload.text : '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\r\n?/g, '\n')
            .replace(/\n/g, '<br>'))
      };
    }
    return PASTE_SANITIZER.sanitizeClipboardPayload({
      html: payload && payload.html ? String(payload.html) : '',
      text: payload && payload.text ? String(payload.text) : '',
      profile: profile || DEFAULT_PASTE_PROFILE,
      targetTagName: activeEditable ? activeEditable.tagName : '',
      targetIsHeading: _isHeadingLikeEditable(activeEditable),
      tokenColors: _getPasteTokenColors()
    });
  }

  function _insertHtmlAtSelection(html, reuseSnapshot) {
    var sel = window.getSelection();
    if (!sel) return null;

    var range = null;
    if (reuseSnapshot) {
      range = restoreRangeSnapshot(activeEditable, reuseSnapshot);
    }
    if (!range) {
      if (!sel.rangeCount) return null;
      range = sel.getRangeAt(0);
      try {
        if (!activeEditable.contains(range.commonAncestorContainer)) return null;
      } catch (e) {
        return null;
      }
    }

    var replacedFragment = null;
    var replacedNodes = [];
    try {
      replacedFragment = range.cloneContents();
      replacedNodes = Array.prototype.slice.call(replacedFragment.childNodes || []);
    } catch (cloneErr) {}

    range.deleteContents();
    var fragment = document.createRange().createContextualFragment(String(html || ''));
    var insertedNodes = Array.prototype.slice.call(fragment.childNodes || []);
    var firstInserted = fragment.firstChild;
    var lastInserted = fragment.lastChild;
    range.insertNode(fragment);

    var pastedRange = null;
    if (firstInserted && lastInserted) {
      pastedRange = document.createRange();
      pastedRange.setStartBefore(firstInserted);
      pastedRange.setEndAfter(lastInserted);
    }

    var caretRange = document.createRange();
    if (lastInserted && lastInserted.parentNode) {
      caretRange.setStartAfter(lastInserted);
    } else {
      caretRange.setStart(range.endContainer, range.endOffset);
    }
    caretRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(caretRange);
    lastRange = caretRange.cloneRange();

    if (!pastedRange) return null;
    return {
      rangeSnapshot: captureRangeSnapshot(activeEditable, pastedRange),
      insertedNodes: insertedNodes,
      replacedNodes: replacedNodes
    };
  }

  function _canReplayLastPaste() {
    if (!activeEditable || !_lastPastePayload) return false;
    var currentEid = _activeEditableEid();
    if (_lastPastePayload.editSessionId !== _activeEditSessionId) return false;
    if (_lastPastePayload.eid !== currentEid) return false;
    return true;
  }

  function _applyPastePayload(payload, profile, replayMode) {
    if (!activeEditable) return null;
    var result = _sanitizePastePayload(payload, profile);
    if (!result || !result.ok) {
      _emitPasteTriageStatus(profile, result || { rejected: true, reason: 'sanitize-failed' });
      return null;
    }

    var pasteBefore = _beginEditOperation();
    var inserted = _insertHtmlAtSelection(
      result.html || '',
      replayMode ? _lastPasteRangeSnapshot : null
    );
    if (!inserted || !inserted.rangeSnapshot) {
      _emitPasteTriageStatus(profile, { rejected: true, reason: 'no-selection' });
      return null;
    }

    _commitEditOperation(pasteBefore);

    _lastPastePayload = {
      html: payload && payload.html ? String(payload.html) : '',
      text: payload && payload.text ? String(payload.text) : '',
      eid: _activeEditableEid(),
      editSessionId: _activeEditSessionId
    };
    _lastPasteRangeSnapshot = inserted.rangeSnapshot;
    _lastPasteInsertedNodes = inserted.insertedNodes || [];
    _lastPasteReplacedNodes = inserted.replacedNodes || [];
    _lastUndonePastePayload = null;
    _focusActiveEditable();
    _emitPasteTriageStatus(profile, result);
    onContentChanged();
    return result;
  }

  function _canRestoreUndonePaste() {
    if (!activeEditable || !_lastUndonePastePayload) return false;
    if (_lastUndonePastePayload.editSessionId !== _activeEditSessionId) return false;
    if (_lastUndonePastePayload.eid !== _activeEditableEid()) return false;
    return true;
  }

  function _redoLastUndonePaste() {
    if (!_canRestoreUndonePaste()) return false;
    var payload = _lastUndonePastePayload;
    _lastUndonePastePayload = null;
    _focusActiveEditable();
    return !!_applyPastePayload(payload, DEFAULT_PASTE_PROFILE, false);
  }

  function _undoLastPasteIfCurrent() {
    if (!activeEditable || !_lastPastePayload || !_lastPasteRangeSnapshot) return false;
    if (_lastPastePayload.editSessionId !== _activeEditSessionId) return false;
    if (_lastPastePayload.eid !== _activeEditableEid()) return false;

    var nodes = Array.isArray(_lastPasteInsertedNodes)
      ? _lastPasteInsertedNodes.filter(function(node) {
        return !!(node && node.parentNode && activeEditable.contains(node));
      })
      : [];
    if (!nodes.length) return false;

    var payload = _lastPastePayload;
    _focusActiveEditable();
    try {
      var firstNode = nodes[0];
      var anchorParent = firstNode.parentNode;
      var anchorOffset = Array.prototype.indexOf.call(anchorParent.childNodes, firstNode);
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i] && nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
      }
      var range = document.createRange();
      if (anchorParent && (anchorParent === activeEditable || activeEditable.contains(anchorParent))) {
        range.setStart(anchorParent, Math.max(0, Math.min(anchorOffset, anchorParent.childNodes.length)));
      } else {
        range.selectNodeContents(activeEditable);
        range.collapse(false);
      }
      range.collapse(true);

      var restoredNodes = Array.isArray(_lastPasteReplacedNodes) ? _lastPasteReplacedNodes : [];
      if (restoredNodes.length) {
        var restoreFragment = document.createDocumentFragment();
        for (var r = 0; r < restoredNodes.length; r++) {
          if (restoredNodes[r]) restoreFragment.appendChild(restoredNodes[r]);
        }
        var firstRestored = restoreFragment.firstChild;
        var lastRestored = restoreFragment.lastChild;
        range.insertNode(restoreFragment);
        if (firstRestored && lastRestored && firstRestored.parentNode && lastRestored.parentNode) {
          range.setStartBefore(firstRestored);
          range.setEndAfter(lastRestored);
        } else {
          range.collapse(true);
        }
      }
      var sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      lastRange = range.cloneRange();
    } catch (err) {
      return false;
    }

    _lastPastePayload = null;
    _lastPasteRangeSnapshot = null;
    _lastPasteInsertedNodes = null;
    _lastPasteReplacedNodes = null;
    _lastUndonePastePayload = payload;
    onContentChanged();
    return true;
  }

  function _replayLastPaste(profile) {
    if (!_canReplayLastPaste()) {
      _emitPasteTriageStatus(profile, { rejected: true, reason: 'paste-context-mismatch' });
      return;
    }
    _applyPastePayload(_lastPastePayload, profile || DEFAULT_PASTE_PROFILE, true);
  }

  document.addEventListener('paste', function(e) {
    if (!activeEditable) return;
    e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    else e.stopPropagation();
    var html = e.clipboardData ? e.clipboardData.getData('text/html') : '';
    var text = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
    if (_plainTextOnly) {
      var plainRange = _getSelectionRangeWithinActiveEditable();
      if (!plainRange) return;
      var plainBefore = _beginEditOperation();
      if (_insertTextAtRange(plainRange, text || '')) {
        _commitEditOperation(plainBefore);
        onContentChanged();
      }
      return;
    }
    var requestedPlainText = Date.now() <= _plainTextPasteRequestedUntil;
    _plainTextPasteRequestedUntil = 0;
    _applyPastePayload(
      { html: html || '', text: text || '' },
      requestedPlainText ? PASTE_PROFILES.PLAIN_TEXT : DEFAULT_PASTE_PROFILE,
      false
    );
  });

  // --- Canvas image drop + paste ---
  // Dropping an image file onto (or pasting a screenshot over) an editable
  // <img> or a CSS-background section replaces it — the file crosses to the
  // parent via structured clone and runs the same upload pipeline as the picker.
  // NOTE: browsers keep dataTransfer.files EMPTY during dragover (protected
  // mode; contents are only exposed on drop), so dragover gates on
  // dataTransfer.types containing 'Files' while drop reads the real FileList.
  function transferCarriesFiles(transfer) {
    var types = transfer && transfer.types;
    if (!types) return false;
    for (var i = 0; i < types.length; i++) {
      if (types[i] === 'Files') return true;
    }
    return false;
  }

  function imageFileFromTransfer(transfer) {
    if (!transfer) return null;
    var files = transfer.files;
    if (!files || !files.length) return null;
    for (var i = 0; i < files.length; i++) {
      var f = files[i];
      if (f && typeof f.type === 'string' && f.type.indexOf('image/') === 0) return f;
    }
    return null;
  }

  function isGraphicKind(kind) {
    return /^(?:photo|logo|icon|decoration|background)$/.test(kind || '');
  }

  // Cheap per-dragover hit test: stamped DOM graphics only. The css-background
  // resolver walks every stylesheet rule, too heavy for continuous dragover;
  // css-background targets still resolve on the actual drop.
  function resolveDropImageElement(evt) {
    var rawTarget = evt.target && evt.target.nodeType === 1 ? evt.target : (evt.target && evt.target.parentElement);
    if (!rawTarget) return null;
    var editable = resolveEditableClickTarget(rawTarget, evt);
    if (editable && isGraphicKind(_kindOf(editable))) {
      return editable;
    }
    return null;
  }

  function resolveImageDropTarget(evt) {
    var editable = resolveDropImageElement(evt);
    if (editable) {
      var path = getEditablePath(editable);
      if (path && isGraphicKind(path.kind)) return path;
    }
    return resolveCssBackgroundClickTarget(evt, null, null);
  }

  var _dropHighlightEl = null;
  function _setDropHighlight(el) {
    if (_dropHighlightEl === el) return;
    if (_dropHighlightEl) _dropHighlightEl.removeAttribute('data-pb-drop-target');
    _dropHighlightEl = el || null;
    if (_dropHighlightEl) _dropHighlightEl.setAttribute('data-pb-drop-target', '');
  }

  document.addEventListener('dragover', function(e) {
    if (_previewMode) return;
    if (!transferCarriesFiles(e.dataTransfer)) return;
    var target = resolveDropImageElement(e);
    _setDropHighlight(target);
    if (!target) {
      // Not over a replaceable image: fall through so the browser shows
      // "not allowed" — but still swallow drops inside sections (handled in
      // drop below) so a miss can't navigate the iframe to the local file.
      var host = e.target && e.target.closest ? e.target : (e.target && e.target.parentElement);
      if (host && host.closest && host.closest('[data-pb-instance]')) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  });

  document.addEventListener('dragleave', function(e) {
    // Only clear when leaving the document entirely (relatedTarget null) or
    // leaving the highlighted element's subtree.
    if (!_dropHighlightEl) return;
    var to = e.relatedTarget;
    if (!to || !_dropHighlightEl.contains(to)) _setDropHighlight(null);
  });

  document.addEventListener('dragend', function() {
    _setDropHighlight(null);
  });

  document.addEventListener('drop', function(e) {
    _setDropHighlight(null);
    if (_previewMode) return;
    if (!transferCarriesFiles(e.dataTransfer)) return;
    var host = e.target && e.target.closest ? e.target : (e.target && e.target.parentElement);
    if (!host || !host.closest || !host.closest('[data-pb-instance]')) return;
    // Block the browser's default "navigate to dropped file" even when the
    // drop misses an image target or carries a non-image file.
    e.preventDefault();
    var file = imageFileFromTransfer(e.dataTransfer);
    if (!file) return;
    var path = resolveImageDropTarget(e);
    if (!path) return;
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    path.droppedFile = file;
    send('image-file-dropped', path);
  });

  document.addEventListener('paste', function(e) {
    if (_previewMode) return;
    if (activeEditable) return; // text-edit paste is handled by the handler above
    var selected = _selectedEditable;
    var file = imageFileFromTransfer(e.clipboardData);
    if (!file) return;
    var path = null;
    if (selected && selected.getAttribute && !isGraphicKind(_kindOf(selected))) selected = null;
    if (selected && selected.getAttribute) {
      path = getEditablePath(selected);
    } else if (_selectedCssBackgroundPath) {
      path = _selectedCssBackgroundPath;
    }
    if (!path || !isGraphicKind(path.kind)) return;
    e.preventDefault();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    path.droppedFile = file;
    send('image-file-dropped', path);
  });
`;
}
